/**
 * Provides a gui for managing content
 * @module content
 */
import React from 'react'
import ImmutablePropTypes from 'react-immutable-proptypes'
import _pick from 'lodash/pick'
import { connect as connectRedux } from 'react-redux'
import { compose } from 'recompose'
import { List } from 'immutable'
import {
  PERM_ACCESS_ADMIN_APP,
  PERM_CREATE_CONTENT,
  PERM_DELETE_CONTENT,
  PERM_UPDATE_CONTENT,
  PERM_VIEW_CONTENT,
  PERM_BULK_UPDATE_CONTENT,
  PERM_VIEW_MEDIA,
} from 'services/auth'
import { CONTENT_MANAGEMENT_PAGE_KEY } from 'services/auth/roles'
import CSParamsDataTable from 'gui/components/content-search-data-table'
import { storeKey as csParamsStoreKey } from 'services/content-search'
import {
  getIdFromLocation,
  getContentPageType,
  CONTENT_ROOTS,
  CONTENT_PAGE_TYPES,
  ID_REGEX,
} from 'services/content'
import RedirectsListDataTable from 'gui/components/url-redirects-data-table'
import ContentDetail from 'gui/components/content-detail'
import ContentCreate from 'gui/components/content-create'
import ContentBulk from 'gui/components/content-bulk'
import ContentList from 'gui/components/content-list'
import ContentListV2 from 'gui/components/content-list-v2'
import Page from 'gui/components/page'
import Link from 'gui/components/link'
import './index.scss'

const redirectsStoreKey = 'redirectsList'

/**
 * The content page component body
 * @param {Object} props The props
 * @param {String} props.location The active navigation
 */
export function ContentPageNavigation ({ location }) {
  const contentPageType = getContentPageType(location)
  return (
    <div className="content-nav">
      <div
        className={`content-nav__item${contentPageType === CONTENT_PAGE_TYPES.LIST_V2 ? ' content-nav__item--active' : ''}`}
      >
        <Link href={`${CONTENT_ROOTS.V2}`}>Content list</Link>
      </div>
      <div
        className={`content-nav__item${contentPageType === CONTENT_PAGE_TYPES.REDIRECTS ? ' content-nav__item--active' : ''}`}
      >
        <Link href={`${CONTENT_ROOTS.BASE}/${CONTENT_PAGE_TYPES.REDIRECTS}`}>Redirects</Link>
      </div>
      <div
        className={`content-nav__item${contentPageType === CONTENT_PAGE_TYPES.SEARCH ? ' content-nav__item--active' : ''}`}
      >
        <Link href={`${CONTENT_ROOTS.BASE}/${CONTENT_PAGE_TYPES.SEARCH}`}>Search</Link>
      </div>
      <div
        className={`content-nav__item${contentPageType === CONTENT_PAGE_TYPES.CREATE ? ' content-nav__item--active' : ''}`}
      >
        <Link href={`${CONTENT_ROOTS.BASE}/${CONTENT_PAGE_TYPES.CREATE}`}>Create</Link>
      </div>
      <div
        className={`content-nav__item${contentPageType === CONTENT_PAGE_TYPES.LIST ? ' content-nav__item--active' : ''}`}
      >
        <Link href={`${CONTENT_ROOTS.BASE}`}>Content List (Deprecated)</Link>
      </div>
      {/*
      <div className={`content-nav__item${active === CONTENT_BULK ?
        'content-nav__item--active' :
        ''}`}>
        <Link href={`${CONTENT_ROOT}/${CONTENT_BULK}`}>Bulk import/update</Link>
      </div>
      */}
    </div>
  )
}

/**
 * The content page component body
 * @param {Object} props The props
 * @param {Map} props.location The location state
 */
function ContentPageBody ({ location }) {
  const contentPageType = getContentPageType(location)

  if (contentPageType === CONTENT_PAGE_TYPES.DETAIL) {
    const id = getIdFromLocation(location)
    if (ID_REGEX.test(id)) {
      return <ContentDetail id={id} />
    }
  }
  switch (contentPageType) {
    case CONTENT_PAGE_TYPES.REDIRECTS:
      return <RedirectsListDataTable storeKey={redirectsStoreKey} />
    case CONTENT_PAGE_TYPES.SEARCH:
      return <CSParamsDataTable storeKey={csParamsStoreKey} />
    case CONTENT_PAGE_TYPES.CREATE:
      return <ContentCreate />
    case CONTENT_PAGE_TYPES.BULK:
      return <ContentBulk />
    case CONTENT_PAGE_TYPES.LIST:
      return <ContentList />
    default:
      return <ContentListV2 />
  }
}

/**
 * React component to display the content page
 * @param {Object} props The props
 * @param {Map} props.location The location state
 */
function ContentPage (props) {
  const { location } = props
  const allowedPermissions = List([
    PERM_ACCESS_ADMIN_APP,
    PERM_CREATE_CONTENT,
    PERM_DELETE_CONTENT,
    PERM_UPDATE_CONTENT,
    PERM_VIEW_CONTENT,
    PERM_VIEW_MEDIA,
    PERM_BULK_UPDATE_CONTENT,
  ])
  return (
    <Page
      className="content"
      allowedPermissions={allowedPermissions}
      allowedRolesKey={CONTENT_MANAGEMENT_PAGE_KEY}
    >
      <h2 className="page__title">Content Management</h2>
      <ContentPageNavigation location={location} />
      <ContentPageBody location={location} />
    </Page>
  )
}

ContentPage.propTypes = {
  location: ImmutablePropTypes.map.isRequired,
}

export default compose(connectRedux((state) => _pick(state, ['location'])))(ContentPage)
